import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="categories--admin--composer-conversion"
//
// Shared by the outreach clone flow (PLA-1510) and the outreach template edit
// flow (PLA-2128) — both ask "convert this legacy content to the new editor?"
// via the same SweetAlert confirmation used everywhere else on these pages.
// Title/text/button copy are Stimulus values so each caller can customize the
// wording without duplicating the dialog wiring below.
export default class extends Controller {
  static values = {
    convertUrl: String,
    legacyUrl: String,
    // The outreach-clone flow's convert/legacy choices are both POSTs to the
    // same clone action (just a different editor_choice param). The template
    // edit flow's legacy choice is a plain GET back to the existing edit
    // page — no backend action needed — so each URL's method is configurable.
    convertMethod: { type: String, default: "post" },
    legacyMethod: { type: String, default: "post" },
    title: { type: String, default: "Outreach conversion" },
    text: {
      type: String,
      default: "This email was created in the legacy editor and will be automatically converted for use in the new editor."
    },
    confirmButtonText: { type: String, default: "Convert" },
    cancelButtonText: { type: String, default: "Use legacy editor" }
  };

  show(event) {
    event.preventDefault();

    const convertUrl = this.convertUrlValue;
    const legacyUrl = this.legacyUrlValue;
    const convertMethod = this.convertMethodValue;
    const legacyMethod = this.legacyMethodValue;

    const proceed = (url, method) => {
      // Matches the brief "Success" checkmark flash every other clone/destroy
      // confirmation on this page shows before navigating away (see the
      // doneFunction callback in override_confirm.js). Navigation is delayed
      // slightly so the animation has time to actually render before the
      // browser starts unloading the page for the redirect.
      window.swal({
        title: "Success",
        type: "success",
        text: "",
        html: true,
        timer: 2000,
        showConfirmButton: false
      });

      if (method.toLowerCase() === "get") {
        window.setTimeout(() => { window.location.href = url; }, 800);
        return;
      }

      const csrfToken = document.querySelector("meta[name=\"csrf-token\"]")?.getAttribute("content");

      const form = document.createElement("form");
      form.method = "post";
      form.action = url;
      form.style.display = "none";

      const tokenInput = document.createElement("input");
      tokenInput.type = "hidden";
      tokenInput.name = "authenticity_token";
      tokenInput.value = csrfToken || "";
      form.appendChild(tokenInput);

      document.body.appendChild(form);
      window.setTimeout(() => form.submit(), 800);
    };

    // SweetAlert 1.x renders every dialog on the page into ONE permanently-
    // shared `.sweet-alert` DOM node (see the "Success" swal() above, which
    // reuses this same node). It only manages the specific elements its own
    // config touches (title, text, native buttons) — anything we append
    // ourselves stays in that shared node forever unless we remove it, and
    // would otherwise leak into every unrelated dialog elsewhere on the page
    // (Clone/Delete confirms, error alerts, etc.) for the rest of the session.
    // Strip any leftovers from a previous open before rendering fresh ones.
    const removeInjectedElements = () => {
      document.querySelectorAll(".composer-conversion-legacy-link, .composer-conversion-close").forEach((el) => el.remove());
    };
    removeInjectedElements();

    // Declared here (and assigned once cancelButton is known, below) rather
    // than after the Convert callback, so that callback can reference it —
    // it only ever runs once the dialog is actually dismissed, well after
    // this whole function body has finished assigning it.
    let cleanup;

    // sweetalert.css's `.sweet-alert button` rule sets background-color
    // (and a couple of other properties) with `!important`, which beats a
    // plain inline style regardless of specificity. An inline `!important`
    // still wins that tie (inline declarations are treated as maximally
    // specific even within the `!important` tier), so every property that
    // rule could clobber has to be set the same way here.
    const setImportant = (element, styles) => {
      Object.entries(styles).forEach(([property, value]) => {
        element.style.setProperty(property, value, "important");
      });
    };

    // Dismissal is an injected X button (below) rather than the native cancel
    // button — the cancel button is only kept (hidden) because Escape is
    // hardcoded by SweetAlert to fire a synthetic click on it;
    // closeOnCancel/the zero-arg callback below make that a true, harmless
    // dismiss.
    window.swal({
      title: this.titleValue,
      text: this.textValue,
      showCancelButton: false,
      confirmButtonText: this.confirmButtonTextValue,
      closeOnConfirm: false,
      closeOnCancel: true
      // eslint-disable-next-line prefer-arrow-callback, func-names
    }, function () {
      // SweetAlert 1.x only invokes this callback on Cancel/Escape if it can
      // detect (by stringifying the function and checking for a literal
      // "function(" prefix followed by a parameter name) that it accepts an
      // argument. Keeping this a zero-arg function means Cancel/Escape never
      // reach here at all — they just close the dialog (closeOnCancel above)
      // — so this body only ever runs for the Convert button.
      cleanup();
      proceed(convertUrl, convertMethod);
    });

    const modal = document.querySelector(".sweet-alert");

    // The native cancel button is hidden (showCancelButton: false above) but
    // still present in the DOM purely so Escape's synthetic click has a target
    // — strip its tabindex so it's excluded from SweetAlert's own Tab-cycle
    // focus trap, which walks every `button[tabindex]` regardless of
    // visibility and would otherwise leave an invisible, focusable stop.
    //
    // This button is the SAME shared `.sweet-alert` node's cancel button that
    // override_confirm.js's swal() calls reuse for every other data-confirm
    // dialog on the page (Clone Outreach, Delete, etc.), so the tabindex
    // strip can't be permanent — it has to be restored on every exit path via
    // cleanup(), or those other dialogs silently lose Cancel from their own
    // Tab-cycle for the rest of this page view.
    const cancelButton = modal?.querySelector("button.cancel");
    const cancelButtonTabIndex = cancelButton?.getAttribute("tabindex") ?? null;
    cleanup = () => {
      removeInjectedElements();
      if (cancelButtonTabIndex === null) {
        cancelButton?.removeAttribute("tabindex");
      } else {
        cancelButton?.setAttribute("tabindex", cancelButtonTabIndex);
      }
    };
    cancelButton?.removeAttribute("tabindex");
    // { once: true } since this same shared cancel button persists across
    // every dialog opened on the page for the rest of this page view —
    // without it, a listener would accumulate here on every open.
    cancelButton?.addEventListener("click", cleanup, { once: true });

    // The legacy-editor choice is inserted as a real sibling of the Convert
    // button (not a second native button) so it reads as a link, not a
    // second call to action.
    const legacyButton = document.createElement("button");
    legacyButton.type = "button";
    legacyButton.tabIndex = 0;
    legacyButton.textContent = this.cancelButtonTextValue;
    legacyButton.className = "composer-conversion-legacy-link";
    // appearance: none strips the browser's native button widget chrome (a
    // rectangular bevel drawn independently of background-color) — without
    // it, a "background" shows through no matter what background/border/
    // box-shadow are set to.
    setImportant(legacyButton, {
      appearance: "none",
      "-webkit-appearance": "none",
      "-moz-appearance": "none",
      outline: "none",
      background: "transparent",
      "background-color": "transparent",
      border: "none",
      "border-radius": "0",
      "box-shadow": "none",
      padding: "10px 16px",
      margin: "26px 5px 0 5px",
      color: "#2563eb",
      "font-size": "17px",
      "font-weight": "500",
      cursor: "pointer"
    });
    legacyButton.addEventListener("mouseenter", () => { legacyButton.style.setProperty("color", "#1e40af", "important"); });
    legacyButton.addEventListener("mouseleave", () => { legacyButton.style.setProperty("color", "#2563eb", "important"); });
    legacyButton.addEventListener("click", () => {
      cleanup();
      proceed(legacyUrl, legacyMethod);
    });
    modal?.querySelector(".sa-button-container")?.insertBefore(legacyButton, cancelButton);

    const closeButton = document.createElement("button");
    closeButton.type = "button";
    closeButton.tabIndex = 0;
    closeButton.setAttribute("aria-label", "Close");
    closeButton.className = "composer-conversion-close";
    setImportant(closeButton, {
      appearance: "none",
      "-webkit-appearance": "none",
      "-moz-appearance": "none",
      outline: "none",
      position: "absolute",
      top: "6px",
      right: "6px",
      margin: "0",
      padding: "4px",
      background: "transparent",
      "background-color": "transparent",
      border: "none",
      "border-radius": "0",
      "box-shadow": "none",
      color: "#9ca3af",
      "line-height": "0",
      cursor: "pointer"
    });
    closeButton.innerHTML = "<svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 24 24\" stroke-width=\"1.5\" stroke=\"currentColor\" style=\"width: 16px; height: 16px;\"><path stroke-linecap=\"round\" stroke-linejoin=\"round\" d=\"M6 18 18 6M6 6l12 12\" /></svg>";
    closeButton.addEventListener("mouseenter", () => { closeButton.style.setProperty("color", "#4b5563", "important"); });
    closeButton.addEventListener("mouseleave", () => { closeButton.style.setProperty("color", "#9ca3af", "important"); });
    // Bypasses proceed() entirely, same as Cancel/Escape above — closing via
    // the X takes no action, leaving the partner exactly where they were.
    closeButton.addEventListener("click", () => {
      cleanup();
      window.swal.close();
    });
    modal?.appendChild(closeButton);
  }
}
